import { useEffect, useState, Fragment } from "react";
import { api, currentYM, dispDate, formatRupiah, todayYMD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/PageLoader";

export default function AdminRekap() {
  const [mode, setMode] = useState("month");
  const [ref, setRef] = useState(currentYM());
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "pegawai")));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { mode, ref };
    if (userId !== "all") params.user_id = userId;
    api.get("/rekap/admin", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mode, ref, userId]);

  const onMode = (v) => {
    setMode(v);
    setRef(v === "month" ? currentYM() : todayYMD());
  };

  const toggle = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));

  const [downloading, setDownloading] = useState(false);
  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const params = { mode, ref };
      if (userId !== "all") params.user_id = userId;
      const res = await api.get("/rekap/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rekap-presensi-${ref}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="overline">Laporan</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Rekap Presensi</h1>
        </div>
        <Button
          onClick={exportXlsx}
          disabled={downloading || !data}
          variant="outline"
          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          data-testid="btn-export-rekap-xlsx"
        >
          {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Export Excel
        </Button>
      </div>

      <Card className="mt-6 p-4 border border-zinc-200 shadow-none">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <p className="overline mb-2">Mode</p>
            <Tabs value={mode} onValueChange={onMode}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="month" data-testid="adm-tab-month">Bulanan</TabsTrigger>
                <TabsTrigger value="week" data-testid="adm-tab-week">Mingguan</TabsTrigger>
                <TabsTrigger value="day" data-testid="adm-tab-day">Harian</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Input
            type={mode === "month" ? "month" : "date"}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            data-testid="adm-ref"
          />
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="adm-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Pegawai</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <p className="text-xs text-zinc-500 mt-3">
            Periode: {dispDate(data.start)} s.d. {dispDate(data.end)}
          </p>
        )}
      </Card>

      {loading && !data && <PageLoader label="Memuat rekap..." />}
      {!(loading && !data) && (

      <Card className="mt-4 border border-zinc-200 shadow-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Pegawai</TableHead>
              <TableHead>Tipe</TableHead>
              <TableHead className="text-right">Telat</TableHead>
              <TableHead className="text-right">Potongan</TableHead>
              <TableHead className="text-right">Lembur</TableHead>
              <TableHead className="text-right">Upah Lembur</TableHead>
              <TableHead className="text-right">PHL Jam</TableHead>
              <TableHead className="text-right">Upah PHL</TableHead>
              <TableHead className="text-right">Netto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.rows || []).map((row) => {
              const isHourly = row.user.pay_type === "hourly";
              const open = !!expanded[row.user.id];
              const visibleDays = row.days.filter((d) => d.in_time || d.on_leave);
              return (
                <Fragment key={row.user.id}>
                  <TableRow data-testid={`rekap-row-${row.user.employee_id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => toggle(row.user.id)}>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        data-testid={`btn-expand-${row.user.employee_id}`}
                        onClick={(e) => { e.stopPropagation(); toggle(row.user.id); }}
                      >
                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{row.user.name}</TableCell>
                    <TableCell>{isHourly ? "PHL" : "Bulanan"}</TableCell>
                    <TableCell className="text-right">{row.summary.total_late_human}</TableCell>
                    <TableCell className="text-right">{formatRupiah(row.summary.total_deduction)}</TableCell>
                    <TableCell className="text-right">{row.summary.total_overtime_human}</TableCell>
                    <TableCell className="text-right">{formatRupiah(row.summary.total_overtime_wage)}</TableCell>
                    <TableCell className="text-right">{row.summary.total_phl_human}</TableCell>
                    <TableCell className="text-right">{formatRupiah(row.summary.total_phl_wage)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatRupiah(row.summary.total_netto)}</TableCell>
                  </TableRow>
                  {open && (
                    <TableRow data-testid={`detail-${row.user.employee_id}`}>
                      <TableCell colSpan={10} className="bg-zinc-50 p-0">
                        <DetailDays days={visibleDays} isHourly={isHourly} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {data && data.rows.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-zinc-500 py-8">Tidak ada data</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      )}

    </div>
  );
}

const DetailDays = ({ days, isHourly }) => {
  if (days.length === 0) {
    return <p className="px-6 py-4 text-sm text-zinc-500">Tidak ada presensi pada periode ini.</p>;
  }
  return (
    <div className="px-6 py-4">
      <p className="overline mb-2">Detail Hari</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead>Tanggal</TableHead>
              <TableHead>Masuk</TableHead>
              <TableHead>Pulang</TableHead>
              {!isHourly && <TableHead className="text-right">Telat</TableHead>}
              {!isHourly && <TableHead className="text-right">Potongan</TableHead>}
              {!isHourly && <TableHead className="text-right">Lembur</TableHead>}
              {!isHourly && <TableHead className="text-right">×</TableHead>}
              {!isHourly && <TableHead className="text-right">Upah Lembur</TableHead>}
              {isHourly && <TableHead className="text-right">Jam Kerja</TableHead>}
              {isHourly && <TableHead className="text-right">Upah PHL</TableHead>}
              <TableHead>Izin</TableHead>
              <TableHead className="text-right">Netto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {days.map((d) => (
              <TableRow key={d.work_date} className="border-zinc-200">
                <TableCell className="font-medium">{dispDate(d.work_date)}</TableCell>
                <TableCell className="font-mono text-xs">{d.in_time?.split(" ")[1] || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{d.out_time?.split(" ")[1] || "—"}</TableCell>
                {!isHourly && <TableCell className={`text-right ${d.late_min > 0 ? "text-red-600" : ""}`}>{d.late_human}</TableCell>}
                {!isHourly && <TableCell className="text-right">{formatRupiah(d.deduction)}</TableCell>}
                {!isHourly && <TableCell className={`text-right ${d.overtime_min > 0 ? "text-green-700" : ""}`}>{d.overtime_human}</TableCell>}
                {!isHourly && <TableCell className="text-right">{d.multiplier !== 1 ? <span className="text-purple-700">×{d.multiplier}</span> : "-"}</TableCell>}
                {!isHourly && <TableCell className="text-right">{formatRupiah(d.overtime_wage)}</TableCell>}
                {isHourly && <TableCell className="text-right">{d.overtime_human}</TableCell>}
                {isHourly && <TableCell className="text-right">{formatRupiah(d.phl_wage)}</TableCell>}
                <TableCell>{d.on_leave ? <span className="text-amber-700 uppercase text-xs font-semibold">{d.leave_type}</span> : "-"}</TableCell>
                <TableCell className="text-right font-semibold">{formatRupiah(d.netto)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
